import PropTypes from 'prop-types';
import { useEffect, useCallback, useRef } from 'react';
import DateRangePickerDropdown from 'components/calendar/date-range-picker-dropdown';
import { ListboxMultiselect, ListboxSelect, Button, Icon } from '@evertrue/et-components';
import EmailSearchController from './filters/email-seach/email-search-controller';
import { GlobalSearchProvider } from 'apps/layout/global-search/global-search-context';
import NameSearchController from './filters/name-search/name-search-controller';
import { Tooltip2 } from '@evertrue/et-components';

const OutreachFilters = ({
  emailTab,
  createdDateRange,
  setCreatedDateRange,
  scheduledDateRange,
  setScheduledDateRange,
  sentDateRange,
  setSentDateRange,
  replyDateRange,
  setReplyDateRange,
  replyStatus,
  setReplyStatus,
  resetPage,
  selectedFilters,
  setSelectedFilters,
  filterOptions,
  setFilterOptions,
  setSelectedEmailAddresses,
  selectedEmailAddresses,
  selectedNames,
  setSelectedNames,
  setFilterType,
}) => {
  const addFilterRef = useRef(null);

  const ClearFilterButton = ({ onClick, title }) => (
    <Button type="simple" onClick={onClick} title={title}>
      <Icon icon={'clear'}></Icon>
    </Button>
  );

  ClearFilterButton.propTypes = {
    onClick: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
  };

  useEffect(() => {
    const baseLabel =
      emailTab === 'Drafts' ? 'Created Date' : emailTab === 'Scheduled' ? 'Scheduled Date' : 'Date You Sent';

    const isSentDateRangeSelected = selectedFilters.some((f) => f.value === 'sentDateRange') && emailTab === 'Sent';
    const isReplyDateRangeSelected = selectedFilters.some((f) => f.value === 'replyDateRange') && emailTab === 'Sent';
    const isRecipientEmailSelected = selectedFilters.some((f) => f.value === 'recipientEmail');
    const isRecipientNameSelected = selectedFilters.some((f) => f.value === 'recipientName');

    const disabledTextForDate = 'Only one Date filter can be applied at a time.';
    const disabledTextForRecipient = 'Only one Recipient filter can be applied at a time.';

    const withTooltip = (label, tooltipText, disabled) =>
      disabled ? (
        <Tooltip2 trigger={<span style={{ cursor: 'not-allowed', opacity: 0.6 }}>{label}</span>}>
          {tooltipText}
        </Tooltip2>
      ) : (
        label
      );

    const options = [
      ...(emailTab === 'Drafts'
        ? [
            {
              label: baseLabel,
              value: 'draftsDateRange',
            },
          ]
        : []),
      ...(emailTab === 'Scheduled'
        ? [
            {
              label: baseLabel,
              value: 'scheduledDateRange',
            },
          ]
        : []),
      ...(emailTab === 'Sent'
        ? [
            {
              label: withTooltip('Date You Sent', disabledTextForDate, isReplyDateRangeSelected),
              value: 'sentDateRange',
              disabled: isReplyDateRangeSelected,
            },
            {
              label: withTooltip('Date You Received Reply', disabledTextForDate, isSentDateRangeSelected),
              value: 'replyDateRange',
              disabled: isSentDateRangeSelected,
            },
            { label: 'Reply Status', value: 'replyStatus' },
          ]
        : []),
      {
        label: withTooltip('Recipient Email', disabledTextForRecipient, isRecipientNameSelected),
        value: 'recipientEmail',
        disabled: isRecipientNameSelected,
      },
      {
        label: withTooltip('Recipient Name', disabledTextForRecipient, isRecipientEmailSelected),
        value: 'recipientName',
        disabled: isRecipientEmailSelected,
      },
    ];

    setFilterOptions(options);
  }, [emailTab, selectedFilters, setFilterOptions]);

  const handleFilterChange = (selected) => {
    const deselectedFilters = selectedFilters.filter(
      (filter) => !selected.some((newFilter) => newFilter.value === filter.value)
    );

    // Reset the values of the deselected filters
    deselectedFilters.forEach((filter) => {
      if (filter.value === 'draftsDateRange') {
        setCreatedDateRange({ from: null, to: null });
      } else if (filter.value === 'scheduledDateRange') {
        setScheduledDateRange({ from: null, to: null });
      } else if (filter.value === 'sentDateRange') {
        setSentDateRange({ from: null, to: null });
      } else if (filter.value === 'replyDateRange') {
        setReplyDateRange({ from: null, to: null });
      } else if (filter.value === 'replyStatus') {
        setReplyStatus('');
      } else if (filter.value === 'recipientEmail') {
        setSelectedEmailAddresses([]);
      } else if (filter.value === 'recipientName') {
        setSelectedNames([]);
      }
    });

    setSelectedFilters(selected);
  };

  const handleEmailAdd = useCallback(
    (emailObject) => {
      // Add email to the selected list if not already there
      setSelectedEmailAddresses((prev) => {
        if (prev.find((e) => e.emailAddress === emailObject.emailAddress)) {
          return prev; // Email already in list
        }
        const newAddresses = [...prev, emailObject];
        // Reset pagination when a new email filter is added
        resetPage();
        return newAddresses;
      });
    },
    [setSelectedEmailAddresses, resetPage]
  );

  const replyOptions = [
    { label: 'I was last to reply', value: 'USER_LAST_TO_REPLY' },
    { label: 'Recipient was last to reply', value: 'RECIPIENT_LAST_TO_REPLY' },
    { label: 'No replies yet', value: 'NO_REPLIES' },
  ];

  const handleReplyStatusChange = (val) => {
    setReplyStatus(val);

    if (val.value === 'USER_LAST_TO_REPLY' || val.value === 'NO_REPLIES') {
      setFilterType('Sent');
    } else if (val.value === 'RECIPIENT_LAST_TO_REPLY') {
      setFilterType('Reply');
    }

    resetPage();
  };

  const handleClearReplyStatus = () => {
    setReplyStatus('');
    setFilterType('');
    resetPage();
  };

  const handleFilterRefresh = useCallback(() => {
    resetPage();
  }, [resetPage]);

  const handleRemoveFilter = (filterToRemove) => {
    setSelectedFilters((prevFilters) => prevFilters.filter((filter) => filter.value !== filterToRemove.value));

    // Reset the value of the removed filter
    if (filterToRemove.value === 'draftsDateRange') {
      setCreatedDateRange({ from: null, to: null });
    } else if (filterToRemove.value === 'scheduledDateRange') {
      setScheduledDateRange({ from: null, to: null });
    } else if (filterToRemove.value === 'sentDateRange') {
      setSentDateRange({ from: null, to: null });
    } else if (filterToRemove.value === 'replyDateRange') {
      setReplyDateRange({ from: null, to: null });
    } else if (filterToRemove.value === 'replyStatus') {
      setReplyStatus('');
    } else if (filterToRemove.value === 'recipientEmail') {
      setSelectedEmailAddresses([]);
    } else if (filterToRemove.value === 'recipientName') {
      setSelectedNames([]);
    }

    resetPage();
  };

  const handleDateFilterChange = (newRange, filterType) => {
    if (emailTab === 'Drafts') {
      setCreatedDateRange(newRange);
    } else if (emailTab === 'Scheduled') {
      setScheduledDateRange(newRange);
    } else if (emailTab === 'Sent') {
      if (filterType === 'Sent') {
        setSentDateRange(newRange);
      } else if (filterType === 'Reply') {
        setReplyDateRange(newRange);
      }
    }
    resetPage();
    setFilterType(filterType);
  };

  const handleClearDateFilter = (filterType) => {
    // Determine the latest applied date filter
    if (filterType === 'Sent' && replyDateRange.from) {
      setFilterType('Reply');
    } else if (filterType === 'Reply' && sentDateRange.from) {
      setFilterType('Sent');
    } else {
      setFilterType('');
    }
  };

  return (
    <div className="outreach-filter-container">
      <div ref={addFilterRef}>
        <ListboxMultiselect
          buttonClassName="add-filter-btn"
          emptyValue="+ Add Filter"
          alwaysShowLabel={true}
          size="compact"
          options={filterOptions}
          onChange={(selected) => {
            handleFilterChange(selected);
            // Close the dropdown after selecting a filter
            addFilterRef.current?.querySelector('.et-listbox--trigger')?.click();
          }}
          values={selectedFilters}
          label="+ Add Filter"
        />
      </div>
      {selectedFilters.some(
        (filter) =>
          (emailTab === 'Drafts' && filter.value === 'draftsDateRange') ||
          (emailTab === 'Scheduled' && filter.value === 'scheduledDateRange') ||
          (emailTab === 'Sent' && filter.value === 'sentDateRange')
      ) && (
        <div className="filter-with-clear">
          <DateRangePickerDropdown
            onDayClick={(newRange) => handleDateFilterChange(newRange, emailTab === 'Sent' ? 'Sent' : emailTab)}
            range={
              emailTab === 'Drafts' ? createdDateRange : emailTab === 'Scheduled' ? scheduledDateRange : sentDateRange
            }
            allowClear={true}
            disableCloseOnDayClick={true}
            onClear={() => handleClearDateFilter('Sent')}
            label={emailTab === 'Drafts' ? 'Created' : emailTab === 'Scheduled' ? 'Scheduled' : 'Date You Sent'}
            triggerClassName="outreach-filter"
          />
          <ClearFilterButton
            onClick={() => {
              handleRemoveFilter({
                value:
                  emailTab === 'Drafts'
                    ? 'draftsDateRange'
                    : emailTab === 'Scheduled'
                      ? 'scheduledDateRange'
                      : 'sentDateRange',
              });
              handleClearDateFilter('Sent');
            }}
            title="Clear Date Range Filter"
          />
        </div>
      )}
      {selectedFilters.some((filter) => filter.value === 'replyDateRange') && emailTab === 'Sent' && (
        <div className="filter-with-clear">
          <DateRangePickerDropdown
            onDayClick={(newRange) => handleDateFilterChange(newRange, 'Reply')}
            range={replyDateRange}
            allowClear={true}
            disableCloseOnDayClick={true}
            onClear={() => handleClearDateFilter('Reply')}
            label="Reply"
            triggerClassName="outreach-filter"
          />
          <ClearFilterButton
            onClick={() => {
              handleRemoveFilter({ value: 'replyDateRange' });
              handleClearDateFilter('Reply');
            }}
            title="Clear Reply Date Range Filter"
          />
        </div>
      )}
      {selectedFilters.some((filter) => filter.value === 'replyStatus') && emailTab === 'Sent' && (
        <div className="filter-with-clear">
          <ListboxSelect
            id="replyStatusListboxSelect"
            options={replyOptions}
            onChange={handleReplyStatusChange}
            size="medium"
            value={replyStatus}
            inputProps={{ 'aria-label': 'Reply Status' }}
          />
          <ClearFilterButton
            onClick={() => {
              handleRemoveFilter({ value: 'replyStatus' });
              handleClearReplyStatus();
            }}
            title="Clear Reply Status Filter"
          />
        </div>
      )}
      {selectedFilters.some((filter) => filter.value === 'recipientEmail') && (
        <div className="filter-with-clear">
          <EmailSearchController
            onEmailAdd={handleEmailAdd}
            onRefresh={handleFilterRefresh}
            selectedAddresses={selectedEmailAddresses}
            emailTab={emailTab}
          />
          <ClearFilterButton
            onClick={() => handleRemoveFilter({ value: 'recipientEmail' })}
            title="Clear Recipient Email Filter"
          />
        </div>
      )}

      {selectedFilters.some((filter) => filter.value === 'recipientName') && (
        <div className="filter-with-clear">
          <GlobalSearchProvider>
            <NameSearchController
              onNameSelect={resetPage}
              selectedAddresses={selectedEmailAddresses}
              emailTab={emailTab}
            />
            <ClearFilterButton
              onClick={() => handleRemoveFilter({ value: 'recipientName' })}
              title="Clear Recipient Name Filter"
            />
          </GlobalSearchProvider>
        </div>
      )}
    </div>
  );
};

OutreachFilters.propTypes = {
  emailTab: PropTypes.string.isRequired,
  resetPage: PropTypes.func.isRequired,
  createdDateRange: PropTypes.object.isRequired,
  setCreatedDateRange: PropTypes.func.isRequired,
  scheduledDateRange: PropTypes.object.isRequired,
  setScheduledDateRange: PropTypes.func.isRequired,
  sentDateRange: PropTypes.object.isRequired,
  setSentDateRange: PropTypes.func.isRequired,
  replyDateRange: PropTypes.object.isRequired,
  setReplyDateRange: PropTypes.func.isRequired,
  selectedFilters: PropTypes.array.isRequired,
  setSelectedFilters: PropTypes.func.isRequired,
  filterOptions: PropTypes.array.isRequired,
  setFilterOptions: PropTypes.func.isRequired,
  selectedEmailAddresses: PropTypes.array.isRequired,
  setSelectedEmailAddresses: PropTypes.func.isRequired,
  selectedNames: PropTypes.array.isRequired,
  setSelectedNames: PropTypes.func.isRequired,
  replyStatus: PropTypes.string.isRequired,
  setReplyStatus: PropTypes.func.isRequired,
  setFilterType: PropTypes.func.isRequired,
};

export default OutreachFilters;
